import { MultiCollapsible, SingleSelect } from "@bonliva-crm/ui";
import {
  ProfessionFilterModalInnerProps,
  encodeValue,
  toDecodedLabelValue,
  getProfessionToValue,
  Option,
  OptionQualification,
  renderSelectType
} from "../../utils";
import { ComponentProps, useMemo, useState } from "react";
import { useGlobalStore } from "@hooks";
import locale from "@bonliva-crm/locale";
import { ExperienceAndJobofferFilter } from "./common/experience-and-joboffer-filter";
import {
  baseOnChange,
  commonCollapsibleProps,
  isCollapsed,
  isGroupSelected,
  filterOption,
  isOptionStandAlone,
  onCollapse
} from "./utils";
import {
  QualificationFilter as QualificationFilterType,
  QualificationFilter,
  SelectType
} from "@bonliva-crm/graphql";
import { isFilterSelected } from "../../../../utils/is-filter-selected";

export const TeacherFilter = (props: ProfessionFilterModalInnerProps) => {
  const store = useGlobalStore();
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);

  const professionObject = useMemo(() => {
    return props?.value?.[getProfessionToValue(props?.value?.professions?.[0])];
  }, [props?.value]);

  if (
    !("areaOfExpertise" in professionObject) ||
    !("qualified" in professionObject) ||
    !("areaSelectType" in professionObject)
  ) {
    return null;
  }

  const valueAOE = professionObject.areaOfExpertise;
  const valueQualified = professionObject.qualified;
  const valueAOEType = professionObject.areaSelectType;
  const areaOfExpertise = store.cache.getAreaOfExpOnProfessions(
    props?.value?.professions
  );

  const options: Option[] = [];

  areaOfExpertise.forEach((area) => {
    if (area.Profession === "Lärare") {
      options.push({
        label: area.Name,
        value: area.Name
      });

      area.Specializations.forEach((specialization) => {
        options.push({
          label: specialization,
          value: encodeValue(area.Name, specialization)
        });
      });
    }
  });

  const onChange = baseOnChange(options, (finalValues) => {
    props.onChange({
      ...props?.value,
      [getProfessionToValue(props?.value?.professions?.[0])]: {
        qualified: valueQualified,
        areaOfExpertise: finalValues.map((x) => x.value),
        areaSelectType: valueAOEType
      },
      experience: !!finalValues?.length,
      interested: finalValues.length > 0 ? props?.value?.interested : "null",
      experienced: finalValues.length > 0 ? props?.value?.experienced : "null"
    });
  });

  const multiSelectProps: ComponentProps<typeof MultiCollapsible<Option>> = {
    ...commonCollapsibleProps,
    inputLabel: locale.AreaOfExpertise,
    placeholder: null,
    options,
    value: toDecodedLabelValue(valueAOE),
    onChange,
    isOptionSelected: isGroupSelected(valueAOE, options, true),
    isIndeterminate: isGroupSelected(valueAOE, options, false),
    filterOption: filterOption(collapsedGroups, false, valueAOE),
    isCollapsed: isCollapsed(collapsedGroups),
    isOptionStandAlone: isOptionStandAlone(options),
    onCollapse: onCollapse(collapsedGroups, setCollapsedGroups),
    showBadge: false,
    isClearable: isFilterSelected(props?.value?.teacherForm.areaOfExpertise),
    onClear: () => {
      props.onChange({
        ...props?.value,
        teacherForm: {
          ...props?.value?.teacherForm,
          areaOfExpertise: [],
          areaSelectType: SelectType.ANY
        },
        experienced: null,
        interested: null
      });
    },
    menuChildren: {
      prependedChildren: renderSelectType(valueAOEType, (t: SelectType) => {
        props.onChange({
          ...props?.value,
          [getProfessionToValue(props?.value?.professions?.[0])]: {
            qualified: valueQualified,
            areaOfExpertise: valueAOE,
            areaSelectType: t
          }
        });
      })
    }
  };

  const optionsQualification = [
    {
      label: locale.Yes,
      value: QualificationFilter.Yes
    },
    {
      label: locale.No,
      value: QualificationFilter.No
    }
  ];

  const singleSelectQualifiedProps: ComponentProps<
    typeof SingleSelect<OptionQualification>
  > = {
    options: optionsQualification,
    value: optionsQualification.find((x) => x.value === valueQualified) ?? null,
    inputLabel: locale.Qualification1,
    placeholder: null,
    onChange: (selected) => {
      props.onChange({
        ...props?.value,
        [getProfessionToValue(props?.value?.professions?.[0])]: {
          qualified: selected.value,
          areaOfExpertise: valueAOE,
          areaSelectType: valueAOEType
        },
        experience: selected.value !== QualificationFilter.Any,
        interested:
          selected.value !== QualificationFilter.Any ? props?.value?.interested : "null",
        experienced:
          selected.value !== QualificationFilter.Any ? props?.value?.experienced : "null"
      });
    },
    showBadge: false,
    isClearable: isFilterSelected(props?.value?.teacherForm.qualified),
    onClear: () => {
      props.onChange({
        ...props?.value,
        teacherForm: {
          ...props?.value?.teacherForm,
          qualified: QualificationFilterType.Any
        },
        experienced: null,
        interested: null
      });
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: true,
    closeMenuOnSelect: true,
    isSearchable: false,
    maxMenuHeight: 600,
    optionType: "radio",
    menuPlacement: "bottom" as const,
    styleProps: { menu: { showBorder: true } }
  };

  return (
    <>
      <MultiCollapsible<Option> {...multiSelectProps} />
      <SingleSelect<OptionQualification> {...singleSelectQualifiedProps} />
      {(valueAOE.length > 0 || valueQualified !== QualificationFilter.Any) && (
        <ExperienceAndJobofferFilter {...props} />
      )}
    </>
  );
};
